export interface StoreMenuConfig {
  menuId: string; // Dutchie retailer ID from environment variable
  legacyMenuId: string; // Legacy menu ID (e.g., '857', '5440') for deals and other legacy systems
}

export interface StoreConfig {
  medical?: StoreMenuConfig;
  recreational?: StoreMenuConfig;
}

// Helper function to get retailer ID from environment variable
const getRetailerId = (storeName: string, menuType: 'MED' | 'REC'): string => {
  const envKey = `REACT_APP_RETAILER_ID_${storeName}_${menuType}`;
  const value = process.env[envKey] || '';

  return value;
};

// Centralized store configuration
// Key format: "state/storeName" (lowercase, hyphenated)
export const STORE_MENU_CONFIG: Record<string, StoreConfig> = {
  // Maryland
  'maryland/olney': {
    medical: {
      menuId: getRetailerId('OLNEY', 'MED'),
      legacyMenuId: '857',
    },
    recreational: {
      menuId: getRetailerId('OLNEY', 'REC'),
      legacyMenuId: '5440',
    }
  },
  
  // Connecticut
  'connecticut/stamford': {
    medical: {
      menuId: getRetailerId('STAMFORD', 'MED'),
      legacyMenuId: '6158',
    },
    recreational: {
      menuId: getRetailerId('STAMFORD', 'REC'),
      legacyMenuId: '6159',
    }
  },
  'connecticut/west-hartford': {
    medical: {
      menuId: getRetailerId('WEST_HARTFORD', 'MED'),
      legacyMenuId: '5796',
    },
    recreational: {
      menuId: getRetailerId('WEST_HARTFORD', 'REC'),
      legacyMenuId: '5797',
    }
  },
  
  // New Jersey
  'new-jersey/voorhees': {
    medical: {
      menuId: getRetailerId('VOORHEES', 'MED'),
      legacyMenuId: '4855',
    },
    recreational: {
      menuId: getRetailerId('VOORHEES', 'REC'),
      legacyMenuId: '5377',
    }
  },
  'new-jersey/river-edge': {
    recreational: {
      menuId: getRetailerId('RIVER_EDGE', 'REC'),
      legacyMenuId: '6247',
    }
  },
  'new-jersey/mount-olive': {
    recreational: {
      menuId: getRetailerId('MT_OLIVE', 'REC'),
      legacyMenuId: '6248',
    }
  },
  'new-jersey/hamilton': {
    recreational: {
      menuId: getRetailerId('HAMILTON', 'REC'),
      legacyMenuId: '696002abeb54a2091b709e0f',
    }
  },

  // Rhode Island
  'rhode-island/exeter': {
    medical: {
      menuId: getRetailerId('EXETER', 'MED'),
      legacyMenuId: '4545',
    },
    recreational: {
      menuId: getRetailerId('EXETER', 'REC'),
      legacyMenuId: '4996',
    }
  },

  // Vermont
  'vermont/essex-junction': {
    recreational: {
      menuId: getRetailerId('ESSEX_JUNCTION', 'REC'),
      legacyMenuId: '6145',
    }
  }
};

// Build comprehensive menu ID to store mapping (for backward compatibility and retailer ID support)
// Maps both legacy menu IDs (like '857', '5440') and retailer IDs to store information
function buildMenuIdToStore(): Record<string, { state: string; store: string; type: 'medical' | 'recreational' }> {
  const mapping: Record<string, { state: string; store: string; type: 'medical' | 'recreational' }> = {};
  
  // Iterate through the store config and map both legacy menu IDs and retailer IDs
  Object.entries(STORE_MENU_CONFIG).forEach(([storeKey, storeConfig]) => {
    const [state, store] = storeKey.split('/');
    
    // Map legacy menu IDs (for backward compatibility)
    if (storeConfig.medical?.legacyMenuId) {
      mapping[storeConfig.medical.legacyMenuId] = { state, store, type: 'medical' };
    }
    
    if (storeConfig.recreational?.legacyMenuId) {
      mapping[storeConfig.recreational.legacyMenuId] = { state, store, type: 'recreational' };
    }
    
    // Map actual retailer IDs (for current Dutchie integration)
    if (storeConfig.medical?.menuId) {
      mapping[storeConfig.medical.menuId] = { state, store, type: 'medical' };
    }
    
    if (storeConfig.recreational?.menuId) {
      mapping[storeConfig.recreational.menuId] = { state, store, type: 'recreational' };
    }
  });
  
  return mapping;
}

// Legacy menu ID to store mapping (for backward compatibility with old URLs)
export const MENU_ID_TO_STORE = buildMenuIdToStore();